import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  EDITABLE_KNOWLEDGE_FIELD,
  isEditableKnowledgeDocument,
  isKnowledgeEditableTrait,
} from '@shared';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import * as fromGenerated from '../../_generated';
import {
  AssistantBuilderFileUploadComponent,
  type AssistantBuilderFileValue,
} from './assistant-builder/assistant-builder-file-upload.component';
import {
  AssistantsActions,
  KnowledgeModalActions,
} from './store/assistants.actions';
import {
  type AssistantsState,
  assistantsFeature,
} from './store/assistants.reducer';

/**
 * The Edit knowledge modal (SB-3650 / T3).
 *
 * A knowledge manager — admin or owner, not the skill's owner — edits the
 * knowledge documents of a skill they do not own, without ever seeing the
 * skill builder. One field, reusing the builder's own upload zone.
 *
 * PRESENTATIONAL AND STORE-DRIVEN, with no inputs: the whole contract is the
 * `knowledgeModal` slice T2 ships, which carries a click-time `AssistantView`
 * snapshot. That is what lets T5 render this same element on the Teammate page
 * with one line of template.
 *
 * THE COMPONENT IS CREATED PER OPENING and that is load-bearing, not
 * incidental. The page's `@if` destroys it on close, so `transactionId`, the
 * seed and the form control all reset by construction. Do NOT hoist it above
 * the `@if` to avoid re-creation: that breaks SB-3650 / KD-31 and the
 * discard-on-cancel behavior in one move.
 *
 * Figma: node 27022:70502 (open, with chips) and 27034:71514 (save disabled on
 * an empty list) of SquadBox — Prototypes. Both show a header carrying only the
 * skill name — no close button — and a footer of cancel plus save.
 */
@Component({
  selector: 'squadbox-knowledge-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    fromSeriousSystem.ModalComponent,
    AssistantBuilderFileUploadComponent,
  ],
  template: `
    @if (field(); as knowledgeField) {
      <sd-modal
        [isModalVisible]="true"
        [modalOptions]="modalOptions()"
        (triggerAction)="handleTriggerAction($event)"
      >
        <squadbox-assistant-builder-file-upload
          [formControl]="filesControl"
          [label]="knowledgeField.labelKey | translate"
          [placeholder]="knowledgeField.placeholderKey | translate"
          [required]="knowledgeField.required"
          [fileSupportedExtensions]="documentUploadSupportedExtensions"
          [transactionId]="transactionId"
          uploadFrom="edit_knowledge_modal"
          filePickerButtonIcon="plus"
        />
      </sd-modal>
    }
  `,
})
export class KnowledgeModalComponent {
  private readonly assistantsStore = inject(Store<AssistantsState>);
  private readonly assistantsService = inject(fromGenerated.AssistantsService);
  private readonly translateService = inject(TranslateService);
  private readonly toastrService = inject(ToastrService);

  public readonly modal = this.assistantsStore.selectSignal(
    assistantsFeature.selectKnowledgeModal,
  );

  /**
   * SB-3650 / KD-31. ONE uuid per modal OPENING, carried by the upload zone
   * AND by the request body — they must be the same value.
   *
   * It is not only the SQS deduplication key: it is also the S3 staging prefix
   * (`${transactionId}/${documentId}/${filename}`), stamped onto every file
   * when the zone configures its store. A uuid minted in the save handler
   * would point the server's HEAD at an empty prefix and 422 every save that
   * carries an upload. Never re-mint it below.
   */
  protected readonly transactionId = uuidv4();

  protected readonly documentUploadSupportedExtensions = [
    ...ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  ];

  /**
   * The trait's descriptor row. `null` only if the slice was opened on a trait
   * this capability excludes, which the pencil's own conjunction already
   * forbids (SB-3650 / KD-2). Render nothing rather than throwing — and do not
   * "handle" it with a fallback row, which would put an excluded trait one bug
   * away from an editable modal.
   */
  protected readonly field = computed(() => {
    const trait = this.modal().assistant?.trait;
    return trait && isKnowledgeEditableTrait(trait)
      ? EDITABLE_KNOWLEDGE_FIELD[trait]
      : null;
  });

  /**
   * The documents this modal started from: live, and a role the capability may
   * edit. Read ONCE, through the shared predicate (SB-3650 / KD-24) — never a
   * local re-spelling of the role rule, and never an `effect`, which would
   * re-seed and discard staged work if the snapshot reference ever moved.
   *
   * The API drops soft-deleted rows from `AssistantView.documents`; the
   * predicate still checks `deletedAt`, and it is what filters the
   * `instruction` skill.md out.
   */
  private readonly seed: AssistantBuilderFileValue[] = (
    this.modal().assistant?.documents ?? []
  )
    .filter((document) => isEditableKnowledgeDocument(document))
    .map((document) => ({
      id: document.uuid,
      name: document.filename,
      extension: document.extension,
      size: document.size,
    }));

  protected readonly filesControl = new FormControl<
    AssistantBuilderFileValue[]
  >(this.seed, { nonNullable: true });

  /**
   * The staged list AS A SIGNAL, because `modalOptions` is a `computed` and
   * `filesControl.value` is not reactive.
   *
   * Without this the Save button never changes: the computed caches its
   * `actions` array and only re-runs when a signal dependency moves, so
   * removing the last chip would leave Save enabled and adding the first one
   * would leave it disabled. `hasBlockingUpload` needs no equivalent — the
   * upload store's `getFiles()` reads its own `state()` signal.
   */
  private readonly stagedFiles = toSignal(this.filesControl.valueChanges, {
    initialValue: this.seed,
  });

  protected readonly isSaving = signal(false);

  private readonly uploadZone = viewChild(AssistantBuilderFileUploadComponent);

  protected readonly modalOptions = computed<fromSeriousSystem.Modals>(() => ({
    // The skill's name, per the acceptance criteria — bound, not translated.
    title: this.modal().assistant?.name ?? '',
    // Figma shows no close button in the header, in either state.
    showCloseButton: false,
    // Deliberately unlike the delete-confirmation modal on the same page: that
    // one discards a confirmation, this one would discard staged uploads that
    // have already left the browser.
    backdropDismiss: false,
    // Figma node 27034:71420 measures 520 wide; its Main section carries the
    // neutral gradient while the header and footer stay white, and the footer
    // carries a top border. `bodyClass` is the same hook and the same gradient
    // `admin-groups-form-modal.component.ts:353` already uses.
    width: 520,
    bodyClass: 'bg-gradient-to-b from-neutral-50 to-neutral-100',
    showFooterSeparator: true,
    actions: [
      {
        label: this.translateService.instant(
          'ASSISTANTS.ASSISTANT_BUILDER.CTA_CANCEL',
        ) as string,
        action: 'cancel',
        type: 'cancel',
        // Disabled while a save is in flight. Closing mid-save destroys this
        // component through the page's `@if`, but `firstValueFrom` keeps the
        // HTTP subscription alive — the write would land anyway, and B1 says
        // Annuler leaves the skill's sources unchanged. Nothing else stops it:
        // the modal has no close button and the backdrop is inert.
        disabled: this.isSaving(),
      },
      {
        label: this.translateService.instant(
          'ASSISTANTS.ASSISTANT_BUILDER.CTA_SAVE',
        ) as string,
        action: 'save',
        type: 'save',
        disabled: this.isSaveDisabled(),
      },
    ],
  }));

  protected handleTriggerAction(action: string): void {
    if (action === 'save') {
      void this.save();
      return;
    }
    this.close();
  }

  /**
   * Three terms, and the third needs a handle this component keeps on purpose.
   *
   * It is NOT a dirty check: B1 requires Save enabled with at least one file
   * even when nothing changed, so a no-op save is a legal 200.
   */
  private isSaveDisabled(): boolean {
    return (
      this.stagedFiles().length === 0 ||
      this.isSaving() ||
      this.hasBlockingUpload()
    );
  }

  /**
   * Defense in depth, mirroring the builder's `hasBlockingComplianceUpload`:
   * the disabled button is the primary gate, but re-read the store so a save
   * fails loud rather than shipping an in-flight, invalid or failed file.
   *
   * The control's value only ever holds persistable statuses, so its length
   * cannot stand in for this. The zone resolves its own store — this modal
   * binds no `uploadStore` — so `getResolvedStore()` is the only reach.
   */
  private hasBlockingUpload(): boolean {
    const store = this.uploadZone()?.getResolvedStore();
    if (!store) {
      return false;
    }
    // An audio file being measured is staged NOWHERE yet, so the loop below
    // cannot see it and a save started in that window would ship a DTO missing
    // the file the user just picked — then close on success, looking saved.
    if (store.hasPendingPreflight()) {
      return true;
    }
    return store
      .getFiles()
      .some(
        (file) =>
          file.status === 'upload-pending' ||
          file.status === 'upload-ongoing' ||
          file.status === 'upload-invalid' ||
          file.status === 'upload-failed',
      );
  }

  private async save(): Promise<void> {
    const assistant = this.modal().assistant;
    const knowledgeField = this.field();
    if (!assistant || !knowledgeField || this.isSaveDisabled()) {
      return;
    }
    this.isSaving.set(true);
    // The DTO below is a SNAPSHOT. `isSaving` only greys the footer, so without
    // this the user can still add or remove a chip while the PUT is in flight;
    // a success then closes the modal and those edits are lost with no notice.
    // `emitEvent: false` keeps the staged-files signal from re-firing on a
    // value that has not changed.
    this.filesControl.disable({ emitEvent: false });
    try {
      await firstValueFrom(
        this.assistantsService.assistantsControllerSetAssistantKnowledgeV1(
          assistant.uuid,
          this.buildDto(knowledgeField.documentRole),
        ),
      );
      // Mirrors the builder: the list reloads, then the modal closes.
      this.assistantsStore.dispatch(AssistantsActions.loadAssistants());
      this.close();
      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (_error) {
      // The server's own message never reaches the user — a refusal may name
      // something outside the caller's read scope (constitution Article III,
      // condition 5). So the toast stays the generic skill-update copy, and the
      // modal closes (SB-3650 / KD-34, the design answer to OQ-3).
      //
      // THREE CONSEQUENCES, ALL ACCEPTED BY THE EPIC OWNER — do not "fix" one
      // of them in isolation:
      //   1. This catch reads no status, so the modal closes on EVERY failure
      //      — a dropped network and a 422 as much as the 403 the design
      //      question was about. Closing only on 403 was the option that was
      //      declined, because it costs the status detection this branch has
      //      deliberately never had.
      //   2. `transactionId` is minted per modal OPENING (KD-31), so files
      //      already staged to S3 under this one are orphaned by the close.
      //      Reopening mints a new prefix; nothing recovers them.
      //   3. The spec's "clear permission error" is therefore NOT delivered.
      //      That deviation is recorded for spec-sync, not forgotten.
      this.toastrService.error(
        this.translateService.instant(
          'ADMIN.MANAGED_SKILLS.TOAST.UPDATE_ERROR',
        ) as string,
        undefined,
        { closeButton: true, disableTimeOut: true },
      );
      this.close();
    } finally {
      this.isSaving.set(false);
      // Paired with the `disable` above, and INERT while both paths close: the
      // page's `@if` has already destroyed this component either way. It is
      // kept because it is the only thing that would stop a re-opened modal
      // from rendering a permanently disabled list if the failure path ever
      // stops closing again.
      this.filesControl.enable({ emitEvent: false });
    }
  }

  /**
   * Replace semantics, computed by PARTITION rather than by a tracked delta:
   * `AssistantBuilderFileValue.id` IS the document uuid, so membership in the
   * seed is the whole answer. A chip the user removed simply stops being in
   * the staged list, and absence is the removal — the endpoint's own contract.
   *
   * `role` is SENT, never omitted: the server defaults an unlabelled document
   * to `regular` (`assistants.helpers.ts:304`), and compliance refuses that
   * (`assistants.validators.ts:704`). The descriptor is what keeps it a lookup
   * rather than a second per-trait condition.
   */
  private buildDto(
    documentRole: fromGenerated.DocumentRole,
  ): fromGenerated.SetAssistantKnowledgeDto {
    const seedIds = new Set(this.seed.map((file) => file.id));
    const staged = this.filesControl.value;

    return {
      transactionId: this.transactionId,
      documentIdsToKeep: staged
        .filter((file) => seedIds.has(file.id))
        .map((file) => file.id),
      documentsToAdd: staged
        .filter((file) => !seedIds.has(file.id))
        .map((file) => ({
          documentId: file.id,
          filename: file.name,
          role: documentRole,
        })),
    };
  }

  private close(): void {
    this.assistantsStore.dispatch(KnowledgeModalActions.close());
  }
}
